import { CodedError } from '@ebu/contracts';
import {
  WORLD_FORMAT,
  WORLD_LIMITS,
  WORLD_SCHEMA_VERSION,
  worldSchema,
  type World,
} from './schema';

/**
 * Validation, migration and (de)serialisation of world documents. Every path that accepts a world
 * from outside the running editor (file import, IndexedDB load) goes through parseWorld /
 * parseWorldFile; saves go through validateWorld. Results are values, not exceptions, so callers
 * decide how to recover; `toImportError` produces the coded error for user-facing failures.
 */

export type WorldParseFailure =
  'too-large' | 'not-json' | 'wrong-format' | 'newer-version' | 'too-many-objects' | 'invalid';

export type WorldParseResult =
  | { ok: true; world: World; migratedFrom: number | null }
  | { ok: false; reason: WorldParseFailure; issues: string[] };

/** Migration from version N to N+1, keyed by N. Operates on untrusted plain JSON. */
export type WorldMigration = (doc: Record<string, unknown>) => Record<string, unknown>;

/** No historical versions exist yet; v1 is the first published schema. */
export const WORLD_MIGRATIONS: Readonly<Record<number, WorldMigration>> = {};

const fail = (reason: WorldParseFailure, issues: string[] = []): WorldParseResult => ({
  ok: false,
  reason,
  issues,
});

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** Upgrades an older document step by step to `targetVersion`. */
export function migrateWorldDocument(
  doc: unknown,
  migrations: Readonly<Record<number, WorldMigration>> = WORLD_MIGRATIONS,
  targetVersion: number = WORLD_SCHEMA_VERSION,
):
  | { ok: true; doc: Record<string, unknown>; migratedFrom: number | null }
  | { ok: false; reason: WorldParseFailure; issues: string[] } {
  if (!isRecord(doc) || doc['format'] !== WORLD_FORMAT) {
    return { ok: false, reason: 'wrong-format', issues: ['format'] };
  }
  const version = doc['schemaVersion'];
  if (typeof version !== 'number' || !Number.isInteger(version) || version < 1) {
    return { ok: false, reason: 'invalid', issues: ['schemaVersion'] };
  }
  if (version > targetVersion) {
    return { ok: false, reason: 'newer-version', issues: ['schemaVersion'] };
  }
  let current = doc;
  for (let v = version; v < targetVersion; v++) {
    const step = migrations[v];
    if (!step) return { ok: false, reason: 'invalid', issues: [`no migration from v${v}`] };
    current = { ...step(current), schemaVersion: v + 1 };
  }
  return { ok: true, doc: current, migratedFrom: version === targetVersion ? null : version };
}

function issuePaths(error: { issues: { path: PropertyKey[] }[] }): string[] {
  // Paths only: values may contain child-authored text and are never echoed into logs.
  return [...new Set(error.issues.map((i) => i.path.map(String).join('.') || '(root)'))].slice(
    0,
    20,
  );
}

/** Validates a current-version world (the save path). No migration. */
export function validateWorld(value: unknown): WorldParseResult {
  if (
    isRecord(value) &&
    Array.isArray(value['objects']) &&
    value['objects'].length > WORLD_LIMITS.maxObjects
  ) {
    return fail('too-many-objects', ['objects']);
  }
  const parsed = worldSchema.safeParse(value);
  return parsed.success
    ? { ok: true, world: parsed.data, migratedFrom: null }
    : fail('invalid', issuePaths(parsed.error));
}

/** Validates an already-parsed document of any known version (the IndexedDB load path). */
export function parseWorld(
  value: unknown,
  migrations: Readonly<Record<number, WorldMigration>> = WORLD_MIGRATIONS,
): WorldParseResult {
  const migrated = migrateWorldDocument(value, migrations);
  if (!migrated.ok) return fail(migrated.reason, migrated.issues);
  const result = validateWorld(migrated.doc);
  return result.ok ? { ...result, migratedFrom: migrated.migratedFrom } : result;
}

/** UTF-8 size without DOM/Node APIs (this package is runtime-neutral). */
export function utf8ByteLength(text: string): number {
  let bytes = 0;
  for (const ch of text) {
    const cp = ch.codePointAt(0)!;
    bytes += cp < 0x80 ? 1 : cp < 0x800 ? 2 : cp < 0x10000 ? 3 : 4;
  }
  return bytes;
}

/** Parses an imported file's text: size limit first, then JSON, version and schema. */
export function parseWorldFile(text: string): WorldParseResult {
  if (text.length > WORLD_LIMITS.maxFileBytes || utf8ByteLength(text) > WORLD_LIMITS.maxFileBytes) {
    return fail('too-large');
  }
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch {
    // Not an exception path: a non-JSON file is an expected, reported import outcome.
    return fail('not-json');
  }
  return parseWorld(json);
}

/** Stable, human-readable export format. parseWorldFile(serializeWorld(w)) round-trips. */
export function serializeWorld(world: World): string {
  return `${JSON.stringify(worldSchema.parse(world), null, 2)}\n`;
}

export function toImportError(result: Extract<WorldParseResult, { ok: false }>): CodedError {
  return new CodedError('WORLD-IMPORT-003', `World import rejected: ${result.reason}`, {
    httpStatus: 400,
    context: { reason: result.reason, issuePaths: result.issues },
  });
}
